import { useGetAdmins } from "@entities/admin";
import { useEditDefaultAgencyPricingPoc } from "@entities/brief/lib/hooks/useEditDefaultAgencyPricingPoc";
import { useEditDefaultPricingPoc } from "@entities/brief/lib/hooks/useEditDefaultPricingPoc";
import { useRemoveAgencyPricingPocFromBrief } from "@entities/brief/lib/hooks/useRemoveAgencyPricingPocFromBrief";
import { useRemovePricingPocFromBrief } from "@entities/brief/lib/hooks/useRemovePricingPocFromBrief";
import useGetBriefPricingAggregate from "@entities/pricingWorkload/lib/hooks/useGetBriefPricingAggregate";
import useGetPricingWorkloads from "@entities/pricingWorkload/lib/hooks/useGetPricingWorkloads";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { Button, Select, Tabs } from "@shared/ui";
import { Users } from "lucide-react";
import { useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import { PocTags } from "./components/PocTags";
import { PocWorkloadTable } from "./components/PocWorkloadTable";
import { PricingStatusSummary } from "./components/PricingStatusSummary";
import styles from "./styles.module.scss";

type BriefPricingTabId = "poc_workload";

interface BriefPricingAggregateProps {
  open: boolean;
  onClose: () => void;
}

export function BriefPricingAggregate({ open }: BriefPricingAggregateProps) {
  const { brief, briefId } = useBriefStore();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [agencySearchQuery, setAgencySearchQuery] = useState<string>("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 250);
  const [debouncedAgencySearchQuery] = useDebounce(agencySearchQuery, 250);

  const defaultPricingPoc = useMemo(() => {
    return brief?.defaultPricingPoc;
  }, [brief?.defaultPricingPoc]);
  const defaultAgencyPricingPoc = useMemo(() => {
    return brief?.defaultAgencyPricingPoc;
  }, [brief?.defaultAgencyPricingPoc]);
  const defaultPricingPocId = defaultPricingPoc?.userId;
  const defaultAgencyPricingPocId = defaultAgencyPricingPoc?.userId;
  const { admins, isFetching: isAdminsFetching } = useGetAdmins(
    50,
    1,
    undefined,
    debouncedSearchQuery,
    undefined,
    undefined,
    undefined,
    false,
    true
  );

  const { admins: agencyAdmins, isFetching: isAgencyAdminsFetching } =
    useGetAdmins(
      50,
      1,
      undefined,
      debouncedAgencySearchQuery,
      undefined,
      undefined,
      undefined,
      false,
      true
    );

  const pricingAllocatedTo = useMemo(
    () => brief?.pricing?.allocatedTo?.map((item) => item.userId) || [],
    [brief?.pricing?.allocatedTo]
  );

  const { briefPricingAggregateData, isBriefPricingAggregateLoading } =
    useGetBriefPricingAggregate({ briefId, enabled: open });

  const { pricingWorkloadData, isPricingWorkloadLoading } =
    useGetPricingWorkloads({
      enabled: open,
    });

  const aggregateData = briefPricingAggregateData?.data;
  const assignedPocs = useMemo(
    () => aggregateData?.assignedPricingPocV2 || [],
    [aggregateData?.assignedPricingPocV2]
  );

  const adminOptions = useMemo(
    () => [
      ...(defaultPricingPoc
        ? [
            {
              label:
                `${defaultPricingPoc.user.firstName ?? ""} ${defaultPricingPoc.user.lastName ?? ""}`.trim(),
              value: defaultPricingPoc.userId,
            },
          ]
        : []),
      ...(admins?.data
        ?.filter((admin) => admin._id !== defaultPricingPoc?.userId)
        .map((admin) => ({
          label: `${admin.firstName ?? ""} ${admin.lastName ?? ""}`.trim(),
          value: admin._id,
        })) ?? []),
    ],
    [admins?.data, defaultPricingPoc]
  );

  const agencyAdminOptions = useMemo(
    () => [
      ...(defaultAgencyPricingPoc
        ? [
            {
              label:
                `${defaultAgencyPricingPoc.user.firstName ?? ""} ${defaultAgencyPricingPoc.user.lastName ?? ""}`.trim(),
              value: defaultAgencyPricingPoc.userId,
            },
          ]
        : []),
      ...(agencyAdmins?.data
        ?.filter((admin) => admin._id !== defaultAgencyPricingPoc?.userId)
        .map((admin) => ({
          label: `${admin.firstName ?? ""} ${admin.lastName ?? ""}`.trim(),
          value: admin._id,
        })) ?? []),
    ],
    [agencyAdmins?.data, defaultAgencyPricingPoc]
  );

  const { editDefaultPricingPoc, isEditingDefaultPricingPoc } =
    useEditDefaultPricingPoc();
  const { removePricingPocFromBrief, isRemovingPricingPocFromBrief } =
    useRemovePricingPocFromBrief();
  const { editDefaultAgencyPricingPoc, isEditingDefaultAgencyPricingPoc } =
    useEditDefaultAgencyPricingPoc();
  const {
    removeAgencyPricingPocFromBrief,
    isRemovingAgencyPricingPocFromBrief,
  } = useRemoveAgencyPricingPocFromBrief();

  const isUpdatingDefaultPoc =
    isEditingDefaultPricingPoc || isRemovingPricingPocFromBrief;

  const isUpdatingDefaultAgencyPoc =
    isEditingDefaultAgencyPricingPoc || isRemovingAgencyPricingPocFromBrief;

  const [activeTab, setActiveTab] = useState<BriefPricingTabId>("poc_workload");

  const handleSelectDefaultPoc = (pocId?: string) => {
    if (!briefId) return;

    if (isUpdatingDefaultPoc) return;

    if (!pocId) {
      if (!defaultPricingPocId) return;
      removePricingPocFromBrief({
        briefId,
        pricingPocUserId: defaultPricingPocId,
      });
      setSearchQuery("");
      return;
    }

    if (pocId === defaultPricingPocId) {
      return;
    }

    editDefaultPricingPoc({
      briefId,
      pricingPocUserId: pocId,
    });
    setSearchQuery("");
  };

  const handleSelectDefaultAgencyPoc = (pocId?: string) => {
    if (!briefId) return;

    if (isUpdatingDefaultAgencyPoc) return;

    if (!pocId) {
      if (!defaultAgencyPricingPocId) return;
      removeAgencyPricingPocFromBrief({
        briefId,
        pricingPocUserId: defaultAgencyPricingPocId,
      });
      setAgencySearchQuery("");
      return;
    }

    if (pocId === defaultAgencyPricingPocId) {
      return;
    }

    editDefaultAgencyPricingPoc({
      briefId,
      pricingPocUserId: pocId,
    });
    setAgencySearchQuery("");
  };

  const tabItems = [
    {
      key: "poc_workload" as const,
      label: "POC workload",
      children: (
        <PocWorkloadTable
          workloads={pricingWorkloadData?.data || []}
          isLoading={isPricingWorkloadLoading}
          pricingAllocatedTo={pricingAllocatedTo}
        />
      ),
    },
  ];

  return (
    <div className={styles.container}>
      <PricingStatusSummary
        data={aggregateData}
        isLoading={isBriefPricingAggregateLoading}
      />

      <PocTags pocs={assignedPocs} />
      <div className={styles.defaultPocCards}>
        <div className={styles.defaultPocCard}>
          <div className={styles.defaultPocHeader}>
            <div className={styles.defaultPocTitle}>
              <Users size={12} />
              Default pricing POC
            </div>
            {defaultPricingPocId ? (
              <Button
                type="link"
                size="small"
                className={styles.clearDefaultButton}
                disabled={isUpdatingDefaultPoc}
                onClick={() => handleSelectDefaultPoc(undefined)}
              >
                Clear default
              </Button>
            ) : null}
          </div>
          <Select
            showSearch
            searchValue={searchQuery}
            style={{ width: "100%" }}
            allowClear
            value={defaultPricingPocId}
            loading={isAdminsFetching || isUpdatingDefaultPoc}
            options={adminOptions}
            optionFilterProp="label"
            placeholder="No default — suggest least-loaded"
            onSearch={setSearchQuery}
            onClear={() => handleSelectDefaultPoc(undefined)}
            onChange={(value) => handleSelectDefaultPoc(value)}
          />
          <p className={styles.defaultPocHint}>
            Pre-selected in <span>Send to pricing</span> for direct creators on
            this brief — works even before contacts or deliverables are saved.
          </p>
        </div>

        <div className={styles.defaultPocCard}>
          <div className={styles.defaultPocHeader}>
            <div className={styles.defaultPocTitle}>
              <Users size={12} />
              Default agency pricing POC
            </div>
            {defaultAgencyPricingPocId ? (
              <Button
                type="link"
                size="small"
                className={styles.clearDefaultButton}
                disabled={isUpdatingDefaultAgencyPoc}
                onClick={() => handleSelectDefaultAgencyPoc(undefined)}
              >
                Clear default
              </Button>
            ) : null}
          </div>
          <Select
            showSearch
            searchValue={agencySearchQuery}
            style={{ width: "100%" }}
            allowClear
            value={defaultAgencyPricingPocId}
            loading={isAgencyAdminsFetching || isUpdatingDefaultAgencyPoc}
            options={agencyAdminOptions}
            optionFilterProp="label"
            placeholder="No default — suggest least-loaded"
            onSearch={setAgencySearchQuery}
            onClear={() => handleSelectDefaultAgencyPoc(undefined)}
            onChange={(value) => handleSelectDefaultAgencyPoc(value)}
          />
          <p className={styles.defaultPocHint}>
            Pre-selected in <span>Send to pricing</span> for agency-managed
            creators on this brief.
          </p>
        </div>
      </div>

      <Tabs
        className={styles.tabs}
        activeKey={activeTab}
        onChange={(key: string) => setActiveTab(key as BriefPricingTabId)}
        items={tabItems}
      />
    </div>
  );
}
